import React, { useState, useEffect } from "react";
import {
  DollarSign,
  TrendingUp,
  Users,
  Activity,
  Search,
  Download,
  Filter,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Eye,
  CheckCircle,
  Clock,
  XCircle,
  RefreshCw
} from "lucide-react";
import api from "../../../api/axiosInstance"; // ⚠️ verify path relative to this file
import "../../../assets/css/admin/earningscommissions.css";

// Backend field names → component field names
const mapTransaction = (t) => ({
  id: t.transaction_code,
  doctor: t.doctor_name,
  patient: t.patient_name,
  service: t.service,
  amount: t.amount,
  commission: t.commission_percentage,
  adminShare: t.admin_share,
  doctorShare: t.doctor_share,
  status: t.status,
  date: t.transacted_at ? t.transacted_at.split(" ")[0] : "",
  time: t.transacted_at ? new Date(t.transacted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : "",
  paymentMethod: t.payment_method,
});

const EarningsCommissions = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterService, setFilterService] = useState("All Services");
  const [filterDate, setFilterDate] = useState("Last 30 Days");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const itemsPerPage = 5;

  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [recordsError, setRecordsError] = useState(null);

  // Fetch transactions from backend
  useEffect(() => {
    let cancelled = false;

    const fetchTransactions = async () => {
      setRecordsLoading(true);
      setRecordsError(null);
      try {
        const res = await api.get("/transactions");
        if (!cancelled) {
          setRecords(res.data.map(mapTransaction));
        }
      } catch (err) {
        console.error("Failed to load transactions:", err);
        if (!cancelled) {
          setRecordsError("Could not load transactions. Please try again later.");
        }
      } finally {
        if (!cancelled) setRecordsLoading(false);
      }
    };

    fetchTransactions();
    return () => { cancelled = true; };
  }, []);

  // Filter records
  const filteredRecords = records
    .filter(r => r.doctor !== "AI Therapy") // Exclude AI Therapy sessions
    .filter(r => activeTab === "All" || r.status === activeTab)
    .filter(r => filterService === "All Services" || r.service.includes(filterService))
    .filter(r =>
      r.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.doctor.toLowerCase().includes(searchTerm.toLowerCase()) ||
      r.patient.toLowerCase().includes(searchTerm.toLowerCase())
    );

  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRecords = filteredRecords.slice(startIndex, startIndex + itemsPerPage);

  // Calculate stats (excluding AI Therapy sessions)
  const doctorRecords = records.filter(r => r.doctor !== "AI Therapy");
  const stats = {
    totalRevenue: doctorRecords.reduce((sum, r) => sum + r.amount, 0),
    doctorEarnings: doctorRecords.reduce((sum, r) => sum + r.doctorShare, 0),
    adminCommissions: doctorRecords.reduce((sum, r) => sum + r.adminShare, 0),
    transactions: doctorRecords.length
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case "Completed": return <CheckCircle size={14} />;
      case "Pending": return <Clock size={14} />;
      case "Failed": return <XCircle size={14} />;
      case "Refunded": return <RefreshCw size={14} />;
      default: return null;
    }
  };

  return (
    <div className="ec-wrapper">
      <div className="ec-container">
        {/* Header */}
        <div className="ec-header">
          <div className="ec-header-content">
            <h1 className="ec-title">Earnings & Commissions</h1>
            <p className="ec-subtitle">Platform revenue, doctor payouts & commission tracking</p>
          </div>
          <button className="ec-btn-export">
            <Download size={18} />
            Export Report
          </button>
        </div>

        {/* Stats Cards */}
        <div className="ec-stats-grid">
          <div className="ec-stat-card">
            <div className="ec-stat-icon ec-stat-blue">
              <DollarSign size={24} />
            </div>
            <div className="ec-stat-content">
              <p className="ec-stat-label">Total Revenue</p>
              <h3 className="ec-stat-value">₨ {stats.totalRevenue.toLocaleString()}</h3>
            </div>
          </div>

          <div className="ec-stat-card">
            <div className="ec-stat-icon ec-stat-green">
              <Users size={24} />
            </div>
            <div className="ec-stat-content">
              <p className="ec-stat-label">Doctors Earnings</p>
              <h3 className="ec-stat-value">₨ {stats.doctorEarnings.toLocaleString()}</h3>
            </div>
          </div>

          <div className="ec-stat-card">
            <div className="ec-stat-icon ec-stat-orange">
              <Activity size={24} />
            </div>
            <div className="ec-stat-content">
              <p className="ec-stat-label">Admin Commissions</p>
              <h3 className="ec-stat-value">₨ {stats.adminCommissions.toLocaleString()}</h3>
            </div>
          </div>

          <div className="ec-stat-card">
            <div className="ec-stat-icon ec-stat-purple">
              <CheckCircle size={24} />
            </div>
            <div className="ec-stat-content">
              <p className="ec-stat-label">Total Transactions</p>
              <h3 className="ec-stat-value">{stats.transactions}</h3>
            </div>
          </div>
        </div>

        {/* Main Card */}
        <div className="ec-main-card">
          {/* Tabs */}
          <div className="ec-tabs">
            {["All", "Completed", "Pending", "Failed", "Refunded"].map(tab => (
              <button
                key={tab}
                className={`ec-tab ${activeTab === tab ? "ec-tab-active" : ""}`}
                onClick={() => {
                  setActiveTab(tab);
                  setCurrentPage(1);
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="ec-filters">
            <div className="ec-search-wrapper">
              <Search size={18} className="ec-search-icon" />
              <input
                type="text"
                placeholder="Search transaction, doctor, or patient..."
                className="ec-search-input"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <select
              className="ec-filter-select"
              value={filterService}
              onChange={(e) => {
                setFilterService(e.target.value);
                setCurrentPage(1);
              }}
            >
              <option>All Services</option>
              <option>AI Quranic Therapy</option>
              <option>Doctor Consultation</option>
            </select>
            <select
              className="ec-filter-select"
              value={filterDate}
              onChange={(e) => setFilterDate(e.target.value)}
            >
              <option>Last 30 Days</option>
              <option>Last 7 Days</option>
              <option>Last 90 Days</option>
              <option>This Year</option>
            </select>
          </div>

          {/* Content Grid */}
          <div className="ec-content-grid">
            {/* Transactions Table */}
            <div className="ec-list-section">
              <h3 className="ec-section-title">Transaction History</h3>

              {recordsLoading ? (
                <div className="ec-empty-state">
                  <Activity size={48} />
                  <p>Loading transactions...</p>
                </div>
              ) : recordsError ? (
                <div className="ec-empty-state">
                  <XCircle size={48} />
                  <p>{recordsError}</p>
                </div>
              ) : filteredRecords.length === 0 ? (
                <div className="ec-empty-state">
                  <Activity size={48} />
                  <p>No transactions found</p>
                </div>
              ) : (
                <>
                  <table className="ec-table">
                    <thead>
                      <tr>
                        <th className="ec-th">ID</th>
                        <th className="ec-th">Provider</th>
                        <th className="ec-th">Patient</th>
                        <th className="ec-th">Amount</th>
                        <th className="ec-th">Status</th>
                        <th className="ec-th">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRecords.map((record) => (
                        <tr key={record.id} className="ec-tr">
                          <td className="ec-td">
                            <span className="ec-ticket-id">{record.id}</span>
                          </td>
                          <td className="ec-td">
                            <span className="ec-provider-name">{record.doctor}</span>
                          </td>
                          <td className="ec-td">
                            <span className="ec-patient-name">{record.patient}</span>
                          </td>
                          <td className="ec-td">
                            <div className="ec-amount-display">
                              <span className={`ec-td-amount ${record.amount === 0 ? 'ec-amount-free' : ''}`}>
                                {record.amount === 0 ? 'Free' : `₨ ${record.amount.toLocaleString()}`}
                              </span>
                              {record.commission > 0 && (
                                <span className="ec-commission-badge">{record.commission}%</span>
                              )}
                            </div>
                          </td>
                          <td className="ec-td">
                            <span className={`ec-status-badge ec-status-${record.status.toLowerCase()}`}>
                              {getStatusIcon(record.status)}
                              {record.status}
                            </span>
                          </td>
                          <td className="ec-td">
                            <button
                              className="ec-btn-view"
                              onClick={() => setSelectedTransaction(record)}
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Pagination */}
                  <div className="ec-pagination">
                    <div className="ec-pagination-info">
                      Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredRecords.length)} of {filteredRecords.length} transactions
                    </div>
                    <div className="ec-pagination-controls">
                      <button
                        className="ec-pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft size={18} />
                      </button>

                      {[...Array(totalPages)].map((_, index) => {
                        const page = index + 1;
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={page}
                              className={`ec-pagination-number ${currentPage === page ? "ec-pagination-number-active" : ""}`}
                              onClick={() => handlePageChange(page)}
                            >
                              {page}
                            </button>
                          );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} style={{padding: '0 8px', color: '#94a3b8'}}>...</span>;
                        }
                        return null;
                      })}

                      <button
                        className="ec-pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Details Panel */}
            <div className="ec-details-panel">
              {selectedTransaction ? (
                <div className="ec-details-content">
                  <h3 className="ec-details-title">Transaction Details</h3>

                  <div className="ec-detail-section">
                    <p className="ec-detail-section-title">Transaction Info</p>
                    <div className="ec-detail-row">
                      <span className="ec-detail-label">Transaction ID:</span>
                      <span className="ec-detail-value">{selectedTransaction.id}</span>
                    </div>
                    <div className="ec-detail-row">
                      <span className="ec-detail-label">Date & Time:</span>
                      <span className="ec-detail-value">{selectedTransaction.date} at {selectedTransaction.time}</span>
                    </div>
                    <div className="ec-detail-row">
                      <span className="ec-detail-label">Service:</span>
                      <span className="ec-detail-value">{selectedTransaction.service}</span>
                    </div>
                    <div className="ec-detail-row">
                      <span className="ec-detail-label">Payment Method:</span>
                      <span className="ec-detail-value">{selectedTransaction.paymentMethod}</span>
                    </div>
                  </div>

                  <div className="ec-detail-section">
                    <p className="ec-detail-section-title">Financial Breakdown</p>
                    <div className="ec-detail-row">
                      <span className="ec-detail-label">Total Amount:</span>
                      <span className="ec-detail-value ec-detail-amount">₨ {selectedTransaction.amount.toLocaleString()}</span>
                    </div>
                    <div className="ec-detail-row">
                      <span className="ec-detail-label">Commission Rate:</span>
                      <span className="ec-detail-value">{selectedTransaction.commission}%</span>
                    </div>
                    <div className="ec-detail-row ec-detail-highlight">
                      <span className="ec-detail-label">Admin Share:</span>
                      <span className="ec-detail-value">₨ {selectedTransaction.adminShare.toLocaleString()}</span>
                    </div>
                    <div className="ec-detail-row ec-detail-highlight">
                      <span className="ec-detail-label">Doctor Share:</span>
                      <span className="ec-detail-value">₨ {selectedTransaction.doctorShare.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="ec-detail-section">
                    <p className="ec-detail-section-title">Participants</p>
                    <div className="ec-detail-row">
                      <span className="ec-detail-label">Doctor:</span>
                      <span className="ec-detail-value">{selectedTransaction.doctor}</span>
                    </div>
                    <div className="ec-detail-row">
                      <span className="ec-detail-label">Patient:</span>
                      <span className="ec-detail-value">{selectedTransaction.patient}</span>
                    </div>
                  </div>

                  <div className="ec-detail-status">
                    <span className={`ec-status-badge ec-status-${selectedTransaction.status.toLowerCase()}`}>
                      {getStatusIcon(selectedTransaction.status)}
                      {selectedTransaction.status}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="ec-no-selection">
                  <Activity size={48} />
                  <p>Select a transaction to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EarningsCommissions;